import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  LayoutDashboard,
  BookOpen,
  Users,
  GraduationCap,
  BellRing,
  Store,
  FileBarChart,
  Settings,
  Sparkles,
  Sprout,
  Hourglass,
  Library,
  Archive,
  Wallet,
  CreditCard,
  Gift,
  BookMarked,
  DoorOpen,
  Type,
  Wrench,
  ChevronRight,
} from "lucide-react";
import {
  BADGE_MEANING,
  type ActionBadgeKey,
} from "@/hooks/useAdminActionBadges";
import { useAdminUnseen } from "@/hooks/useAdminUnseen";
import { useInstructorInsights } from "@/hooks/useInstructorInsights";


interface Item {
  title: string;
  url: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
  /**
   * Page-specific actionable badge. Omitted on purpose for pages that have no
   * trustworthy "waiting for you" count (Staffing is read-only, Tax Center has
   * no reliable tax-action source yet, Bring a Friend has no pending state,
   * Insights/Studio/History are informational). Those pages show no number
   * rather than a duplicated section count.
   */
  badge?: ActionBadgeKey;
}

interface Group {
  id: string;
  label: string;
  items: Item[];
}

// Destinations are unchanged — only their presentation is grouped.
const executiveDesk: Item = {
  title: "Executive Desk",
  url: "/admin",
  icon: LayoutDashboard,
  end: true,
};

const groups: Group[] = [
  {
    id: "operations",
    label: "Operations",
    items: [
      { title: "Greenhouse", url: "/admin/greenhouse", icon: Sprout },
      { title: "Waiting On", url: "/admin/waiting", icon: Hourglass, badge: "waiting_on" },
      { title: "Knowledge Vault", url: "/admin/knowledge", icon: Library },
      { title: "Insights", url: "/admin/reports", icon: FileBarChart },
    ],
  },
  {
    id: "classes",
    label: "Classes",
    items: [
      { title: "Classes", url: "/admin/classes", icon: BookOpen, badge: "classes" },
      { title: "Enrollment Alerts", url: "/admin/enrollment-alerts", icon: BellRing, badge: "enrollment_alerts" },
      { title: "Registrations", url: "/admin/registrations", icon: Users, badge: "registrations" },
      { title: "Bring a Friend", url: "/admin/referrals", icon: Gift },
      { title: "Account Credits", url: "/admin/account-credits", icon: CreditCard },
      { title: "Open House", url: "/admin/open-house", icon: DoorOpen, badge: "open_house" },
      { title: "Pop-Up", url: "/admin/popup", icon: Store },
    ],
  },
  {
    id: "team",
    label: "Team",
    items: [
      { title: "Instructors", url: "/admin/instructors", icon: GraduationCap, badge: "instructors" },
      { title: "Payroll", url: "/admin/payroll", icon: Wallet, badge: "payroll" },
      { title: "Tax Center", url: "/admin/tax", icon: FileBarChart },
    ],
  },
  {
    id: "finance",
    label: "Finance",
    items: [
      { title: "Financial Ledger", url: "/admin/ledger", icon: BookMarked, badge: "ledger" },
    ],
  },
  {
    id: "records",
    label: "Records",
    items: [
      { title: "History", url: "/admin/archived-alerts", icon: Archive },
    ],
  },
  {
    id: "studio",
    label: "Studio",
    items: [
      { title: "Executive Playbook", url: "/admin/playbook", icon: BookMarked },
      { title: "Content Studio", url: "/admin/studio/content", icon: Type },
      { title: "MAVIS Builder", url: "/admin/studio/builder", icon: Wrench },
      { title: "Studio", url: "/admin/settings", icon: Settings },
    ],
  },
];

const allItems: Item[] = [executiveDesk, ...groups.flatMap((g) => g.items)];

const STORAGE_KEY = "ss-admin-sidebar-groups";

function formatBadge(n: number): string {
  if (n > 99) return "99+";
  return String(n);
}

function readStored(): Record<string, boolean> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

export function AdminSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const { pathname } = useLocation();
  // Badges count items THIS admin has not reviewed yet — not the total number
  // of open records. See src/hooks/useAdminUnseen.tsx.
  const { counts: badges } = useAdminUnseen();

  const { proposalsWaitingCount } = useInstructorInsights();

  const isActive = (url: string, end?: boolean) =>
    end ? pathname === url : pathname === url || pathname.startsWith(url + "/");

  const groupHasActive = (g: Group) => g.items.some((i) => isActive(i.url, i.end));

  const [open, setOpen] = useState<Record<string, boolean>>(() => {
    const stored = readStored();
    const next: Record<string, boolean> = {};
    for (const g of groups) next[g.id] = stored[g.id] ?? (g.id === "operations" || g.id === "classes");
    return next;
  });

  // Navigating into a group's child route auto-expands that group.
  useEffect(() => {
    setOpen((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const g of groups) {
        if (groupHasActive(g) && !next[g.id]) {
          next[g.id] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(open));
    } catch {
      /* session storage unavailable — expansion simply won't persist */
    }
  }, [open]);

  const renderItem = (item: Item) => {
    const count = item.badge ? badges[item.badge] ?? 0 : 0;
    const hasBadge = count > 0;
    const isInstructorsItem = item.title === "Instructors" && item.url === "/admin/instructors";
    const baseLabel = isInstructorsItem
      ? `Instructors \u00b7 Proposals${proposalsWaitingCount > 0 ? ` (${proposalsWaitingCount})` : ""}`
      : item.title;
    const label = hasBadge && item.badge
      ? `${baseLabel}, ${count} ${BADGE_MEANING[item.badge]}`
      : baseLabel;
    return (
      <SidebarMenuItem key={item.title}>
        <SidebarMenuButton asChild isActive={isActive(item.url, item.end)} tooltip={label}>
          <NavLink to={item.url} end={item.end} className="flex items-center gap-2 relative" aria-label={label}>
            <span className="relative shrink-0">
              <item.icon className="h-4 w-4" />
              {hasBadge && collapsed && (
                <span
                  className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-destructive text-destructive-foreground text-[9px] font-bold flex items-center justify-center leading-none"
                  aria-hidden="true"
                >
                  {formatBadge(count)}
                </span>
              )}
            </span>
            {!collapsed && (
              <>
                <span className="flex-1 truncate">{baseLabel}</span>
                {hasBadge && (
                  <span
                    className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center leading-none"
                    aria-hidden="true"
                  >
                    {formatBadge(count)}
                  </span>
                )}
              </>
            )}
          </NavLink>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarContent>
        <div className="flex items-center gap-2 px-3 py-4 border-b border-sidebar-border">
          <Sparkles className="h-5 w-5 text-primary shrink-0" />
          {!collapsed && (
            <div className="min-w-0">
              <p className="font-heading text-sm font-bold text-sidebar-foreground truncate">Silver Sage</p>
              <p className="text-[10px] text-muted-foreground truncate">Mission Control</p>
            </div>
          )}
        </div>

        {/* Icon-rail mode keeps every destination one click away, ungrouped. */}
        {collapsed ? (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>{allItems.map(renderItem)}</SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : (
          <>
            <SidebarGroup>
              <SidebarGroupLabel>Administration</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>{renderItem(executiveDesk)}</SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            {groups.map((g) => {
              const isOpen = !!open[g.id];
              const alert = g.items.some((i) => (i.badge ? badges[i.badge] ?? 0 : 0) > 0);
              return (
                <Collapsible
                  key={g.id}
                  open={isOpen}
                  onOpenChange={(v) => setOpen((prev) => ({ ...prev, [g.id]: v }))}
                >
                  <SidebarGroup className="py-1">
                    <CollapsibleTrigger asChild>
                      <SidebarGroupLabel
                        asChild
                      >
                        <button
                          type="button"
                          aria-expanded={isOpen}
                          aria-controls={`admin-nav-group-${g.id}`}
                          className="w-full flex items-center gap-1.5 rounded-md hover:bg-sidebar-accent/60 transition-colors"
                        >
                          <ChevronRight
                            className={`h-3.5 w-3.5 shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`}
                            aria-hidden="true"
                          />
                          <span className="flex-1 text-left truncate">{g.label}</span>
                          {!isOpen && alert && (
                            <span
                              className="h-1.5 w-1.5 rounded-full bg-destructive shrink-0"
                              aria-label={`${g.label} has items needing attention`}
                            />
                          )}
                        </button>
                      </SidebarGroupLabel>
                    </CollapsibleTrigger>
                    <CollapsibleContent id={`admin-nav-group-${g.id}`}>
                      <SidebarGroupContent>
                        <SidebarMenu>{g.items.map(renderItem)}</SidebarMenu>
                      </SidebarGroupContent>
                    </CollapsibleContent>
                  </SidebarGroup>
                </Collapsible>
              );
            })}
          </>
        )}
      </SidebarContent>
    </Sidebar>
  );
}
